import { Bell as BellIcon, X as CloseIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { api } from '../../api/client';
import type { Notification } from '../../api/types';

const POLL_INTERVAL = 60000; // 60 seconds

export function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const pollIntervalRef = useRef<number | null>(null);

  // Fetch notifications with optional abort signal
  const fetchNotifications = async (signal?: AbortSignal) => {
    try {
      const notifs = await api.getNotifications({ signal });
      setNotifications(notifs);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      // eslint-disable-next-line no-console
      console.error('Failed to load notifications:', err);
    }
  };

  // Polling effect (mount-only)
  useEffect(() => {
    const controller = new AbortController();

    fetchNotifications(controller.signal);
    pollIntervalRef.current = window.setInterval(
      () => fetchNotifications(controller.signal),
      POLL_INTERVAL,
    );

    return () => {
      controller.abort();
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Click-outside handler
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleOpen = async () => {
    setIsOpen(true);
    setIsLoading(true);
    try {
      await fetchNotifications();
    } finally {
      setIsLoading(false);
    }
  };

  const handleDismiss = async (id: string) => {
    try {
      await api.dismissNotification(id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to dismiss notification:', err);
    }
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleOpen}
        className="relative rounded-lg p-2 text-slate-500 hover:bg-slate-100"
        aria-label="Notifications"
      >
        <BellIcon className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute right-1 top-1 inline-flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full z-50 mt-2 w-80 rounded-lg border border-slate-200 bg-white shadow-lg">
          <div className="border-b border-slate-200 px-4 py-3">
            <h3 className="font-semibold text-slate-900">Notifications</h3>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="flex justify-center px-4 py-8">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-slate-500">
                <p>No notifications</p>
              </div>
            ) : (
              <div>
                {notifications.map((notif) => (
                  <div
                    key={notif.id}
                    className="border-b border-slate-100 px-4 py-3 hover:bg-slate-50"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">{notif.title}</p>
                        <p className="text-sm text-slate-600">{notif.message}</p>
                        <p className="mt-1 text-xs text-slate-400">
                          {new Date(notif.created_at * 1000).toLocaleDateString()}
                        </p>
                      </div>
                      <button
                        onClick={() => handleDismiss(notif.id)}
                        className="text-slate-400 hover:text-slate-600"
                        aria-label="Dismiss"
                      >
                        <CloseIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
